/* eslint-disable max-len */
import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { appRoutePaths } from 'src/services/route.service';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectGradesAndPromotionsFormViewModel,
  selectGradesAndPromotionsLoadingFormViewModel,
  selectSchoolYear,
  setGradesAndPromotionsSchoolYear,
  setSchoolYearFilter,
} from '../../../../redux/slices/gradesAndPromotionsSlice';
import { fetchGradesAndPromotionsSchoolYears } from '../../../../redux/thunks/gradesAndPromotionsThunks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { ButtonSize } from '../../../../types/ButtonSize';
import { FormDropdownSelectOption } from '../../../../types/propTypes/FormDropdownPropTypes';
import { ReactSelectOption } from '../../../../types/ReactSelectOption';
import { isStateNonAdmin } from '../../../../utilities/userUtilities';
import StudentPage from '../StudentPage';
import './GradesAndPromotionsList.css';

const GradesAndPromotionsList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formView = useAppSelector(selectGradesAndPromotionsFormViewModel);
  const isFormViewLoading = useAppSelector(
    selectGradesAndPromotionsLoadingFormViewModel
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const schoolYear = useAppSelector(selectSchoolYear);
  const isLoading = isFormViewLoading;

  const selectedOption = formView?.selectedSchoolYearOption;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSchoolYearList = formView?.schoolYearOptions || [];
  const schoolYearLabel = formView?.selectedSchoolYearOption?.label;

  // Fetch school year options first
  useEffect(() => {
    const promise: unknown = dispatch(fetchGradesAndPromotionsSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    // Only set default value if we have school year options and no current selection
    if (
      getSchoolYearList &&
      getSchoolYearList.length > 0 &&
      (!schoolYearLabel || schoolYearLabel === '')
    ) {
      const defaultValue = getSchoolYearList[0];
      if (defaultValue) {
        dispatch(
          setGradesAndPromotionsSchoolYear({
            value: defaultValue.value,
            label: defaultValue.text,
          })
        );
        dispatch(setSchoolYearFilter(defaultValue.text));
      }
    }
  }, [
    dispatch,
    formView?.schoolYearOptions,
    getSchoolYearList,
    schoolYearLabel,
  ]);

  const handleOnChange = async (
    option: FormDropdownSelectOption
  ): Promise<void> => {
    const selectedValue = (option as SingleValue<ReactSelectOption>)?.value;
    const selectedLabel = (option as SingleValue<ReactSelectOption>)?.label;
    if (selectedValue && selectedLabel) {
      dispatch(
        setGradesAndPromotionsSchoolYear({
          value: selectedValue,
          label: selectedLabel,
        })
      );
      dispatch(setSchoolYearFilter(selectedLabel));
    }
  };

  const handleSearch = (): void => {
    dispatch(setSchoolYearFilter(formView?.selectedSchoolYearOption?.label));
    navigate(
      appRoutePaths.GradesAndPromotionsInput(
        formView?.selectedSchoolYearOption?.label
      )
    );
  };

  return (
    <StudentPage
      pageClass="data-input--student--grades-and-promotions--list"
      isLoading={isLoading}
      loadingDataId={'grades-and-promotions-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <div
              className={
                isStateNonAdmin()
                  ? 'school-year-selection-container school-year-padding'
                  : 'school-year-selection-container'
              }
            >
              <StyledSingleDropdown
                fieldName="schoolYear"
                options={getSchoolYearList}
                onChange={handleOnChange}
                value={selectedOption}
                isSearchable={false}
              />
            </div>
            <div className="search-button">
              {
                <ActionButton
                  classes="button--secondary"
                  size={ButtonSize.SMALL}
                  onClick={handleSearch}
                  dataTestId="student-search-button"
                  tooltipText="Click to search for student"
                >
                  <>Search</>
                </ActionButton>
              }
            </div>
          </div>
          <div className="subheading-container-padding">
            <div data-testid={'component-heading-text'}>
              Click the Search button to search for students by MSIS ID.
            </div>
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr></tr>
              </thead>
              <tbody></tbody>
            </table>
          </div>
        </div>
      </div>
    </StudentPage>
  );
};

export default GradesAndPromotionsList;
